/**
 * SalviaMarketIntelligence V55 — INRP, distribuição de risco, INRP por setor, forecast
 */
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Brain, TrendingUp, BarChart3 } from "lucide-react";
import { GaugeChart, InfoTooltip, IndicatorManualModal } from "@/components/home";

export default function SalviaMarketIntelligence() {
  usePageTitle("Inteligência de Mercado");
  const { user } = useAuth();

  const { data, isLoading, error } = trpc.dashboardsAdv.salvia.marketIntelligence.useQuery(undefined, {
    enabled: !!user && user.role === "admin",
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Inteligência de Mercado">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;

  return (
    <SalviaLayout title="Dashboard — Inteligência de Mercado">
      <div className="space-y-6">
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full" />)}</div>
        ) : (
          <>
            {/* INRP Gauge + KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Card className="p-6 flex flex-col items-center">
                <div className="flex items-center gap-2 mb-2">
                  <Brain size={18} className="text-[#002F59]" />
                  <span className="text-sm font-medium">INRP</span>
                  <InfoTooltip label="" formula="INRP = (AVG(score) / 25) × 100" description="Índice Nacional de Risco Psicossocial" />
                </div>
                <GaugeChart value={data.inrp.value} label="INRP" />
              </Card>
              <Card className="p-6 text-center">
                <div className="text-sm text-muted-foreground mb-2">Total de Avaliações</div>
                <div className="text-3xl font-bold text-[#002F59] tabular-nums">{data.inrp.totalEvaluations.toLocaleString("pt-BR")}</div>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-sm text-muted-foreground mb-2">Score Médio Global</div>
                <div className="text-3xl font-bold text-[#002F59] tabular-nums">{data.inrp.avgScore.toFixed(1)}</div>
                <div className="text-xs text-muted-foreground">de 25</div>
              </Card>
            </div>

            {/* Risk Distribution */}
            <Card className="p-6">
              <h3 className="text-sm font-semibold text-[#002F59] mb-4">Distribuição de Risco Global</h3>
              {data.riskDistribution.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem dados de classificação disponíveis</p>
              ) : (
                <div className="space-y-3">
                  {data.riskDistribution.map((r: any, i: number) => {
                    const total = data.riskDistribution.reduce((s: number, x: any) => s + x.count, 0);
                    const pct = total > 0 ? (r.count / total) * 100 : 0;
                    return (
                      <div key={i} className="flex items-center gap-4">
                        <div className="flex items-center gap-2 w-32">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: r.color }} />
                          <span className="text-sm font-medium">{r.label}</span>
                        </div>
                        <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full rounded" style={{ width: `${pct}%`, backgroundColor: r.color }} />
                        </div>
                        <span className="text-sm tabular-nums w-16 text-right">{r.count} ({pct.toFixed(0)}%)</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* INRP by Sector */}
            {data.inrpBySector && data.inrpBySector.length > 0 && (
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 size={18} className="text-[#002F59]" />
                  <h3 className="text-sm font-semibold text-[#002F59]">INRP por Setor</h3>
                </div>
                <div className="space-y-2">
                  {data.inrpBySector.map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-40 truncate">{s.sector}</span>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.min(s.inrp, 100)}%`,
                            backgroundColor: s.inrp >= 60 ? "#ef4444" : s.inrp >= 40 ? "#f59e0b" : "#22c55e",
                          }}
                        />
                      </div>
                      <span className="text-xs tabular-nums w-12 text-right">{s.inrp.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Risk Forecast */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} className="text-[#002F59]" />
                <h3 className="text-sm font-semibold text-[#002F59]">Previsão de Risco</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Tendência</div>
                  <div className={`text-lg font-bold ${data.trend === "worsening" ? "text-red-600" : data.trend === "improving" ? "text-emerald-600" : "text-amber-600"}`}>
                    {data.trend === "worsening" ? "↑ Piorando" : data.trend === "improving" ? "↓ Melhorando" : data.trend === "stable" ? "→ Estável" : "Dados insuficientes"}
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs text-muted-foreground">INRP Projetado</div>
                  <div className="text-lg font-bold text-[#002F59] tabular-nums">{data.projectedInrp != null ? data.projectedInrp : "—"}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Meses de Dados</div>
                  <div className="text-lg font-bold text-[#002F59]">{data.riskForecast.length}</div>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
      <IndicatorManualModal profile="salvia" />
    </SalviaLayout>
  );
}
